'use client';

import { useEffect, useRef, useCallback, useState } from 'react';

interface TurnstileInstance {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      size?: 'normal' | 'compact';
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile: TurnstileInstance;
    onTurnstileLoad?: () => void;
  }
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById(TURNSTILE_SCRIPT_ID) && window.turnstile) {
      resolve();
      return;
    }

    window.onTurnstileLoad = () => resolve();

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });
}

export function useTurnstile(siteKey: string, enabled: boolean = true) {
  const [token, setToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!siteKey || !enabled) return;

    let mounted = true;

    // The container ref may not be attached yet on the same tick the dialog
    // opens (Radix mounts DialogContent via a portal), so poll briefly until
    // it is. Bails out via `mounted` if the dialog is closed first.
    const tryRender = () => {
      if (!mounted) return;
      if (!containerRef.current) {
        requestAnimationFrame(tryRender);
        return;
      }
      if (widgetIdRef.current) return;

      loadTurnstileScript().then(() => {
        if (!mounted || !containerRef.current || widgetIdRef.current) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (t: string) => {
            if (mounted) setToken(t);
          },
          'expired-callback': () => {
            if (mounted) setToken(null);
          },
          'error-callback': () => {
            if (mounted) setToken(null);
          },
          theme: 'auto',
        });
      });
    };

    tryRender();

    return () => {
      mounted = false;
      setToken(null);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, enabled]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { token, reset, containerRef };
}
